"use client";

import useLoginModal from "@/app/hooks/useLoginModal";
import { SafeUser, SimplifiedUser } from "@/app/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import CurrencySelector, {
  CurrencyOption,
} from "@/app/components/inputs/CurrencySelector";
import axios from "axios";
import toast from "react-hot-toast";
import { useRouter } from "next/navigation";
import { MdCurrencyExchange } from "react-icons/md";
import useCurrencies from "@/app/hooks/useCurrencies";
import useCurrencyStore from "@/app/hooks/useCurrencyStore";

interface CurrencyProps {
  currentUser?: SimplifiedUser | null;
  isOpen: boolean;
  toggleOpen: () => void;
}

const CurrencyMenu: React.FC<CurrencyProps> = ({
  currentUser,
  isOpen,
  toggleOpen,
}) => {
  const loginModal = useLoginModal();
  const router = useRouter();
  const { selectedCurrency, setSelectedCurrency } = useCurrencyStore();

  const [userCurrencySelector, setUserCurrencySelector] =
    useState<CurrencyOption | null>(null);
  const { getByCode } = useCurrencies();

  const userCurrency = useMemo(() => {
    return currentUser && currentUser?.currency
      ? getByCode(currentUser.currency)
      : null;
  }, [currentUser, getByCode]);

  useEffect(() => {
    setUserCurrencySelector(selectedCurrency || userCurrency || null);
  }, [userCurrency, selectedCurrency]);

  const handleOpenCurrencySelector = useCallback(() => {
    toggleOpen();
  }, [toggleOpen]);

  const handleClearCurrency = async () => {
    if (currentUser?.id) {
      try {
        setUserCurrencySelector(null);
        setSelectedCurrency(null);
        await axios.put(
          `/api/updatecurrency/${currentUser.id}`,
          {
            currency: null,
          },
        );
        toast.success("Currency cleared");
        router.refresh();
        toggleOpen();
      } catch (error) {
        toast.error("Error clearing currency.");
      }
    } else {
      setUserCurrencySelector(null);
      setSelectedCurrency(null);
      toggleOpen();
    }
  };

  const handleCurrencyChange = async (value: CurrencyOption | null) => {
    setUserCurrencySelector(value);
    setSelectedCurrency(value);

    if (currentUser?.id && value) {
      try {
        await axios.put(
          `/api/updatecurrency/${currentUser.id}`,
          {
            currency: value.code,
          },
        );
        toast.success("Currency updated");
        router.refresh();
        toggleOpen();
      } catch (error) {
        toast.error("Error updating currency.");
      }
    } else {
      // Guest user: persist selection locally
      toggleOpen();
    }
  };

  return (
    <div className="relative">
      <div
        className="flex flex-row items-center justify-center p-2 cursor-pointer rounded-full hover:bg-gray-100 transition"
        onClick={handleOpenCurrencySelector}
      >
        {userCurrencySelector ? (
          <span className="text-sm text-gray-700 font-semibold">
            {userCurrencySelector.symbol === "$" && userCurrency?.symbol === "$"
              ? `${userCurrencySelector.symbol} ${userCurrencySelector.code}`
              : userCurrencySelector.symbol}
          </span>
        ) : (
          <MdCurrencyExchange size={19} className="text-gray-500" />
        )}
      </div>

      {isOpen && (
        <div
          className="
            absolute 
            right-0 
            mt-2 
            w-64 
            sm:w-72 
            bg-white 
            rounded-2xl 
            shadow-xl 
            border 
            border-gray-100 
            z-50
          "
        >
          <div className="px-4 pt-3 pb-2 border-b border-gray-100">
            <div className="text-sm font-semibold text-gray-900">
              Select your currency
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Prices will be shown using this currency.
            </div>
          </div>
          <div className="px-4 py-3 max-h-[260px] overflow-y-auto">
            <CurrencySelector
              value={userCurrencySelector}
              onChange={handleCurrencyChange}
            />
          </div>
          <div className="px-4 pb-3 flex justify-end">
            <button
              className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100 cursor-pointer"
              onClick={handleClearCurrency}
            >
              Clear currency
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrencyMenu;
